import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import { 
  CheckCircle,
  XCircle,
  SpinnerGap,
  Warning,
  Lightning,
  ShieldCheck,
  Brain,
  Target,
  ArrowRight,
  Star,
  Fire,
  Clock,
  CreditCard,
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const handleCheckout = async (tier, navigate) => {
  try {
    const origin = window.location.origin;
    const { data } = await axios.post(`${API}/subscription/checkout`, {
      tier, origin_url: origin,
    }, { withCredentials: true });
    if (data.url) {
      window.location.href = data.url;
    }
  } catch (err) {
    const msg = err.response?.data?.detail || 'Failed to start checkout';
    alert(msg);
  }
};

// Feature row component
const Feature = ({ included, text }) => (
  <div className="flex items-start gap-2.5 py-1">
    {included ? (
      <CheckCircle weight="fill" className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
    ) : (
      <XCircle weight="fill" className="w-4 h-4 text-muted-foreground/30 flex-shrink-0 mt-0.5" />
    )}
    <span className={cn("text-sm", included ? "text-foreground" : "text-muted-foreground/50 line-through")}>{text}</span>
  </div>
);


export const SubscriptionPage = () => {
  const { user, refreshUser } = useAuth();
  const { pricing, tier, displayNames } = useFeatures();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPaymentStatus = async (sessionId, attempt) => {
    if (attempt >= 5) { setPaymentStatus('timeout'); return; }
    setPaymentStatus('checking');
    try {
      const { data } = await axios.get(`${API}/subscription/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        await refreshUser();
        searchParams.delete('session_id');
        setSearchParams(searchParams, { replace: true });
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch {
      setPaymentStatus('error');
    }
  };

  const isCurrentTier = (t) => tier === t;
  const canUpgradeTo = (t) => {
    const order = ['free', 'standard', 'premium'];
    return order.indexOf(t) > order.indexOf(tier);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="subscription-page">
      {/* Payment Status Banner */}
      {paymentStatus && (
        <div className={cn(
          "border-b px-4 py-3",
          paymentStatus === 'success' && "bg-success/10 border-success/30",
          paymentStatus === 'checking' && "bg-accent/10 border-accent/30",
          (paymentStatus === 'error' || paymentStatus === 'expired') && "bg-destructive/10 border-destructive/30",
          paymentStatus === 'timeout' && "bg-amber-500/10 border-amber-500/30"
        )} data-testid="payment-status-banner">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            {paymentStatus === 'success' && <><CheckCircle weight="fill" className="w-5 h-5 text-success" /><span className="font-bold text-success">Payment successful! Your plan has been upgraded.</span></>}
            {paymentStatus === 'checking' && <><SpinnerGap className="w-5 h-5 text-accent animate-spin" /><span className="text-accent">Verifying your payment...</span></>}
            {paymentStatus === 'error' && <><Warning weight="fill" className="w-5 h-5 text-destructive" /><span className="text-destructive">Error verifying payment. Please contact support.</span></>}
            {paymentStatus === 'expired' && <><Warning weight="fill" className="w-5 h-5 text-destructive" /><span className="text-destructive">Payment session expired. Please try again.</span></>}
            {paymentStatus === 'timeout' && <><Warning weight="fill" className="w-5 h-5 text-amber-500" /><span className="text-amber-500">Taking longer than expected. Check back shortly.</span></>}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-10 md:py-16">
        {/* Hero Headline */}
        <div className="text-center mb-12" data-testid="pricing-hero">
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter mb-4 leading-tight">
            Pass your theory test faster<br className="hidden sm:block" />
            <span className="text-accent"> — without the stress</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-base md:text-lg leading-relaxed mb-4">
            Join thousands of learners using smart practice, real exam simulations, and AI-powered explanations to pass first time.
          </p>
          <div className="inline-flex items-center gap-2 bg-success/10 border border-success/30 text-success px-4 py-2 rounded-sm text-sm font-bold">
            <Target weight="fill" className="w-4 h-4" />
            Most learners are ready in 1–2 weeks
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-testid="pricing-cards">
          {/* FREE PLAN */}
          <div className={cn(
            "border-2 rounded-sm p-6 flex flex-col",
            isCurrentTier('free') ? "border-accent/40 bg-accent/5" : "border-border"
          )} data-testid="plan-free">
            <div className="mb-4">
              <h3 className="font-heading font-bold text-lg">Free</h3>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-black">£0</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Get started and see how it works — no commitment.
              </p>
            </div>
            <div className="space-y-1 flex-1 mb-6">
              <Feature included text="Limited daily practice questions" />
              <Feature included text="1–2 categories unlocked" />
              <Feature included text="Preview mock test" />
              <Feature included={false} text="Full question bank" />
              <Feature included={false} text="Smart review system" />
              <Feature included={false} text="Hazard perception" />
              <Feature included={false} text="AI explanations" />
            </div>
            {isCurrentTier('free') ? (
              <Button variant="outline" className="w-full rounded-sm" disabled data-testid="current-plan-free">
                Current Plan
              </Button>
            ) : (
              <Button variant="outline" className="w-full rounded-sm" disabled>
                Your Plan
              </Button>
            )}
            <p className="text-xs text-muted-foreground text-center mt-2">No card required</p>
          </div>

          {/* CORE PLAN */}
          <div className={cn(
            "border-2 rounded-sm p-6 flex flex-col",
            isCurrentTier('standard') ? "border-blue-500/50 bg-blue-500/5" : "border-blue-500/30"
          )} data-testid="plan-core">
            <div className="mb-4">
              <h3 className="font-heading font-bold text-lg">{displayNames.standard || 'Core'} Plan</h3>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-black">£{pricing.standard || '4.99'}</span>
                <span className="text-sm text-muted-foreground">/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Everything you need to pass your theory test confidently.
              </p>
            </div>
            <div className="space-y-1 flex-1 mb-6">
              <Feature included text="Full question bank (all categories)" />
              <Feature included text="Unlimited practice" />
              <Feature included text="Realistic mock exams" />
              <Feature included text="Smart review (spaced repetition)" />
              <Feature included text="Bookmark difficult questions" />
              <Feature included={false} text="Hazard perception training" />
              <Feature included={false} text="AI-powered explanations" />
            </div>
            {isCurrentTier('standard') ? (
              <Button variant="outline" className="w-full rounded-sm border-blue-500/30" disabled data-testid="current-plan-core">
                Current Plan
              </Button>
            ) : canUpgradeTo('standard') ? (
              <Button onClick={() => handleCheckout('standard', navigate)} className="w-full rounded-sm bg-blue-600 hover:bg-blue-700 text-white" data-testid="checkout-core">
                Unlock Full Access – £{pricing.standard || '4.99'}/mo
              </Button>
            ) : (
              <Button variant="outline" className="w-full rounded-sm" disabled>
                Included in your plan
              </Button>
            )}
            <p className="text-xs text-muted-foreground text-center mt-2">Cancel anytime</p>
          </div>

          {/* PREMIUM / PASS FASTER PLAN */}
          <div className={cn(
            "border-2 rounded-sm p-6 flex flex-col relative overflow-hidden",
            isCurrentTier('premium') ? "border-accent/60 bg-accent/5" : "border-accent/40"
          )} data-testid="plan-premium">
            {/* Popular badge */}
            <div className="absolute top-0 right-0 bg-accent text-accent-foreground text-[10px] font-black uppercase tracking-wider px-3 py-1">
              Most Popular
            </div>
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <Fire weight="fill" className="w-5 h-5 text-accent" />
                <h3 className="font-heading font-bold text-lg">{displayNames.premium || 'Pass Faster'}</h3>
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-black">£{pricing.premium || '9.99'}</span>
                <span className="text-sm text-muted-foreground">/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                For learners who want to pass quickly and understand everything.
              </p>
            </div>
            <div className="space-y-1 flex-1 mb-6">
              <Feature included text="Everything in Core" />
              <Feature included text="Hazard perception training" />
              <Feature included text="AI-powered explanations (learn why, not just answers)" />
              <Feature included text="Faster learning & higher pass confidence" />
            </div>
            {isCurrentTier('premium') ? (
              <Button variant="outline" className="w-full rounded-sm border-accent/30" disabled data-testid="current-plan-premium">
                Current Plan
              </Button>
            ) : (
              <Button onClick={() => handleCheckout('premium', navigate)} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-bold" data-testid="checkout-premium">
                <Fire weight="fill" className="w-4 h-4 mr-1" />
                Pass Faster – £{pricing.premium || '9.99'}/mo
              </Button>
            )}
            <p className="text-xs text-muted-foreground text-center mt-2">Most users pass within 14 days</p>
          </div>
        </div>

        {/* ONE-TIME PASS */}
        <div className="border-2 border-amber-500/30 rounded-sm p-6 mb-12 bg-amber-500/5 max-w-lg mx-auto" data-testid="plan-one-time">
          <div className="flex items-center gap-2 mb-2">
            <Clock weight="fill" className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading font-bold text-lg">One-Time Pass</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-sm">Best for quick learners</span>
          </div>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-3xl font-black">£{pricing.one_time || '7.99'}</span>
            <span className="text-sm text-muted-foreground"> – 30 days full access</span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            No subscription. Full access. Pass and move on.
          </p>
          <Button
            onClick={() => handleCheckout('one_time', navigate)}
            className="w-full rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-bold"
            disabled={tier === 'premium'}
            data-testid="checkout-one-time"
          >
            <CreditCard weight="fill" className="w-4 h-4 mr-2" />
            Get 30-Day Access
          </Button>
        </div>

        {/* TRUST SECTION */}
        <div className="border border-border rounded-sm p-8 mb-12 bg-card" data-testid="trust-section">
          <h2 className="font-heading font-bold text-xl text-center mb-6">Why learners choose us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
            <div className="flex items-start gap-3">
              <ShieldCheck weight="fill" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <span className="text-sm">DVSA-style questions & mock exams</span>
            </div>
            <div className="flex items-start gap-3">
              <Brain weight="fill" className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Learn faster with smart repetition</span>
            </div>
            <div className="flex items-start gap-3">
              <Target weight="fill" className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <span className="text-sm">Track your progress clearly</span>
            </div>
            <div className="flex items-start gap-3">
              <Star weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Built for real test success</span>
            </div>
          </div>
        </div>

        {/* FINAL PUSH CTA */}
        <div className="text-center pb-8" data-testid="final-cta">
          <Lightning weight="fill" className="w-8 h-8 text-accent mx-auto mb-3" />
          <h2 className="font-heading font-bold text-2xl md:text-3xl mb-2">
            You're closer than you think.
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto mb-5">
            Start practicing today and take the guesswork out of passing your theory test.
          </p>
          {tier === 'free' ? (
            <Button onClick={() => navigate('/practice')} className="rounded-sm px-6 py-3 text-base font-bold" data-testid="final-cta-btn">
              Start Free — Upgrade Anytime <ArrowRight weight="bold" className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={() => navigate('/practice')} variant="outline" className="rounded-sm px-6 py-3 text-base font-bold" data-testid="final-cta-btn">
              Continue Practicing <ArrowRight weight="bold" className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
